import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { SearchLocation, StateResponse } from '../../api/api';
import { useCategoryState } from '../category/category-state';
import { apiInstance, useGlobalState, useSessionState } from '../global-state';
import { useIpc } from '../utils/ipc-utils';

export function useLocationQuery(searchValue: string) {
  const globalState = useGlobalState();
  const session = useSessionState();
  const category = useCategoryState();

  // This query fetches locations by search value.
  return useQuery({
    queryKey: ['location-search', session.session_id, category.id, searchValue],
    queryFn: async () => {
      const { data } = await apiInstance.v4.locationsGet(
        globalState.checkout_session_id,
        session.session_id,
        category.id,
        { query: searchValue }
      );
      return data.locations ?? [];
    },
    enabled: searchValue.length >= 3,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/*
 * Some location returned in the search might not be connected to current
 * session and in consequence not displayed in the list. We need to add it
 * then both to the list and to the session.
 */
export function useLocationMutation() {
  const ipc = useIpc();
  const globalState = useGlobalState();
  const session = useSessionState();
  const category = useCategoryState();

  return useMutation({
    mutationFn: async (newSearchLocation: SearchLocation) => {
      const { data } = await apiInstance.v4.addLocationPost(
        globalState.checkout_session_id,
        session.session_id,
        category.id,
        {
          location_ref: newSearchLocation.id,
          shipping_method: newSearchLocation.shipping_method!, // BE
        }
      );
      return data;
    },
    onSuccess: (newState: StateResponse) => {
      // This is triggered in fullscreen - sync state to master.
      ipc.send({
        name: 'sync_state',
        data: newState,
      });
    },
  });
}
